import React from 'react';
const { useState, useEffect } = React;
import { DIAGNOSTIC_FORMS, t } from '../../diagnosticConfig.js';
import { adminCall, cardSt, labelSt, mutedSt, warnSt, tableSt, thSt, tdSt, fmtInt, fmtNum, fmtPct } from './shared.jsx';

// Equipo: inventory crosses and team aggregates, both computed on the server.
// Only aggregates reach the browser; below the minimum group size the server
// sends "not available" instead of numbers.

const itemQ = (key) => DIAGNOSTIC_FORMS.inventory.sections.find(s => s.key === 'tools').itemQuestions.find(q => q.key === key);
const optLabel = (q, key) => (key ? t(q.options.find(o => o.key === key)?.label) || key : '—');
const POLICY = { written: 'Política escrita', informal: 'Acuerdos informales', no: 'Sin política' };

function Unavailable({ x, what = 'personas' }) {
  return <div style={mutedSt}>No disponible: hacen falta al menos {x.minimum} {what} (hay {x.n ?? x.pairs ?? x.teamSize ?? 0}).</div>;
}

export default function TeamInsights({ diagnosticId, run }) {
  const [data, setData] = useState(null);
  const load = () => run(async () => setData(await adminCall({ action: 'insights', diagnosticId })));
  useEffect(() => { load(); }, [diagnosticId]);
  if (!data) return null;
  const { inventory: inv, team } = data;
  const ai = inv.aiPolicy;

  return (
    <div>
      <div style={{ textAlign: 'right', marginBottom: 8 }}>
        <button className="action-btn" style={{ fontSize: 12 }} onClick={load}>↻ Recalcular</button>
      </div>

      {ai.level !== 'none' && (
        <div style={ai.level === 'alert' ? { ...cardSt, borderColor: 'rgba(239,68,68,0.5)', background: 'rgba(239,68,68,0.06)' } : warnSt}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>
            {ai.level === 'alert' ? '⚠️ IA sin política' : '⚠️ IA con acuerdos solo informales'}
          </div>
          <div style={{ fontSize: 13 }}>
            {fmtInt(ai.aiUsers)} de {fmtInt(ai.respondents)} personas usan IA para trabajar; {fmtInt(ai.dontKnowRules)} no conocen reglas sobre qué datos de clientes se pueden poner.
            {ai.clientDataAllowed === 'undefined' && ' No está definido si se permite poner información de clientes.'}
          </div>
        </div>
      )}

      <div style={cardSt}>
        <div style={{ ...labelSt, marginBottom: 4 }}>Licencias pagadas vs. uso declarado</div>
        <div style={{ ...mutedSt, marginBottom: 10 }}>
          Uso según {fmtInt(inv.respondents)} autoevaluaciones enviadas. Costo sin uso = costo mensual × licencias sin uso / licencias.
          {ai.policy && ` Política de IA: ${POLICY[ai.policy] || ai.policy}.`}
        </div>
        {!inv.hasInventory ? <div style={mutedSt}>Todavía no hay un inventario enviado.</div> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={tableSt}>
              <thead><tr>{['Herramienta', 'Licencias', 'Costo/mes', 'La usan (dicen)', 'Sin uso', 'Costo sin uso/mes', 'Según el founder'].map(h => <th key={h} style={thSt}>{h}</th>)}</tr></thead>
              <tbody>
                {inv.tools.map(x => (
                  <tr key={x.category}>
                    <td style={tdSt}><b>{x.label}</b>{x.name ? <div style={mutedSt}>{x.name}</div> : null}</td>
                    <td style={tdSt}>{fmtNum(x.licenses)}</td>
                    <td style={tdSt}>{x.monthlyCost !== null ? `${fmtNum(x.monthlyCost, 2)} ${x.currency}` : x.costText || '—'}</td>
                    <td style={tdSt}>{x.users === null ? <span style={mutedSt}>sin dato de uso</span> : `${x.users} de ${inv.respondents}`}</td>
                    <td style={tdSt}>{fmtNum(x.unusedLicenses)}</td>
                    <td style={{ ...tdSt, fontWeight: x.unusedMonthlyCost ? 700 : 400 }}>{x.unusedMonthlyCost !== null ? `${fmtNum(x.unusedMonthlyCost, 2)} ${x.currency}` : '—'}</td>
                    <td style={tdSt}>{optLabel(itemQ('whoUses'), x.whoUses)}<div style={mutedSt}>{optLabel(itemQ('ifCancelled'), x.ifCancelled)}</div></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {inv.totalUnusedMonthlyCost !== null && (
              <div style={{ textAlign: 'right', fontSize: 13, marginTop: 8 }}>Total sin uso: <b>{fmtNum(inv.totalUnusedMonthlyCost, 2)} {inv.totalCurrency}</b> por mes</div>
            )}
          </div>
        )}
      </div>

      <div style={cardSt}>
        <div style={{ ...labelSt, marginBottom: 4 }}>Clima y motivación del equipo</div>
        <div style={{ ...mutedSt, marginBottom: 12 }}>Solo agregados. Ni siquiera esta vista recibe las respuestas individuales.</div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>Motivadores (cuántos lo ponen en su top 3)</div>
            {team.motivators.available ? team.motivators.top3.map(m => (
              <Bar key={m.key} label={m.label} value={m.count} max={team.motivators.n} />
            )) : <Unavailable x={team.motivators} />}
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>Cómo terminan la semana</div>
            {team.weekEnd.available ? team.weekEnd.options.map(o => (
              <Bar key={o.key} label={o.label} value={o.count} max={team.weekEnd.n} />
            )) : <Unavailable x={team.weekEnd} />}
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>¿El manager acierta los motivadores?</div>
            {team.managerAccuracy.available ? (
              <div style={{ fontSize: 13 }}>
                Acierta en promedio <b>{fmtNum(team.managerAccuracy.avgHits, 1)} de 3</b> ({fmtPct(team.managerAccuracy.rate)}), sobre {team.managerAccuracy.pairs} personas.
              </div>
            ) : <Unavailable x={team.managerAccuracy} what="personas con ambas respuestas" />}
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>Riesgo de salida</div>
            {team.exitRisk.available ? (
              <div style={{ fontSize: 13 }}>
                <b>{team.exitRisk.present ? 'Hay' : 'No hay'}</b> riesgo de salida en el equipo.
                <div style={mutedSt}>Registrado en {team.exitRisk.assessed} de {team.exitRisk.teamSize} 1:1. Nunca se muestra con nombre.</div>
              </div>
            ) : <Unavailable x={team.exitRisk} what="personas en el equipo" />}
          </div>
        </div>
      </div>
    </div>
  );
}

function Bar({ label, value, max }) {
  const pct = max ? Math.round((value / max) * 100) : 0;
  return (
    <div style={{ marginBottom: 6 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}><span>{label}</span><span>{value}</span></div>
      <div style={{ height: 6, background: 'rgba(30,42,44,0.06)', borderRadius: 3 }}>
        <div style={{ width: `${pct}%`, height: 6, background: 'var(--globant-green)', borderRadius: 3 }} />
      </div>
    </div>
  );
}
